import GLightbox from 'glightbox';

const ROOT_SELECTOR = '.product-information__grid--fullscreen';
// Scope to slides in the live carousel only. The legacy <zoom-dialog> also
// renders <li class="product-media-container--zoomable"> for each media item
// internally — including those would duplicate every image in the lightbox.
const ZOOMABLE_SELECTOR = 'slideshow-slide.product-media-container--zoomable';
const DESKTOP_ZOOM_LEVEL = 2.5;
const DESKTOP_MQ = '(min-width: 750px)';

class FullscreenProductZoom {
  constructor() {
    this.lightbox = null;
    document.addEventListener('click', this.handleClick.bind(this), true);
    this.disarmAll();
    this.observeForNewSlides();
  }

  /**
   * Strip the theme's `on:click="#zoom-dialog-.../open/N"` attribute from every
   * zoomable container inside our fullscreen layout. The theme's component
   * dispatcher (assets/component.js, capture-phase listener at document)
   * registers before our own listener, so the only way to stop it from opening
   * the legacy <zoom-dialog> is to remove the attribute it keys off of.
   */
  disarm(container) {
    if (!container || container.dataset.fsZoomDisarmed === '1') return;
    container.dataset.fsZoomDisarmed = '1';
    container.removeAttribute('on:click');
  }

  disarmAll(scope = document) {
    scope
      .querySelectorAll(`${ROOT_SELECTOR} ${ZOOMABLE_SELECTOR}`)
      .forEach((c) => this.disarm(c));
  }

  /**
   * Variant changes can re-render the gallery slides — newly inserted
   * `.product-media-container--zoomable` elements arrive armed with
   * a fresh `on:click`. Watch the document and disarm any that appear.
   */
  observeForNewSlides() {
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(ZOOMABLE_SELECTOR) && node.closest(ROOT_SELECTOR)) {
            this.disarm(node);
          } else if (node.querySelectorAll) {
            this.disarmAll(node);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  handleClick(event) {
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (!root) return;

    const container = event.target.closest(ZOOMABLE_SELECTOR);
    if (!container) return;

    // Block the existing on:click="#zoom-dialog/open/..." handler from firing.
    event.preventDefault();
    event.stopImmediatePropagation();

    this.open(root, container);
  }

  buildElements(root) {
    const containers = Array.from(root.querySelectorAll(ZOOMABLE_SELECTOR));
    return {
      containers,
      elements: containers
        .map((container) => {
          const img = container.querySelector('img');
          if (!img) return null;
          // Prefer the explicit high-res URL the gallery already computes
          // (3840px wide, see snippets/product-media.liquid). Fall back to
          // currentSrc → src for safety.
          const href = img.dataset.maxResolution || img.currentSrc || img.src;
          if (!href) return null;
          return { href, type: 'image', alt: img.alt || '' };
        })
        .filter(Boolean),
    };
  }

  open(root, clickedContainer) {
    const { containers, elements } = this.buildElements(root);
    if (elements.length === 0) return;

    const index = Math.max(0, containers.indexOf(clickedContainer));

    // Destroy any previous instance — variant changes can leave a stale one.
    this.destroy();

    this.lightbox = GLightbox({
      elements,
      touchNavigation: true,
      keyboardNavigation: true,
      loop: false,
      effect: 'fade',
      openEffect: 'fade',
      closeEffect: 'fade',
      startAt: index,
      zoomable: true,
      draggable: true,
      preload: true,
    });

    this.lightbox.on('slide_after_load', ({ slide }) => {
      if (slide) this.enhanceSlide(slide);
    });

    this.lightbox.on('close', () => {
      this.lightbox = null;
    });

    this.lightbox.open();
  }

  enhanceSlide(slideEl) {
    const img = slideEl.querySelector('img');
    if (!img || img.dataset.fsZoomReady === '1') return;
    img.dataset.fsZoomReady = '1';

    // Make sure mobile pinch-zoom isn't blocked by GLightbox's pan rules.
    img.style.touchAction = 'pinch-zoom';

    if (!this.isDesktop()) return;

    // Desktop: click-to-zoom toggle with cursor-positioned origin.
    img.style.cursor = 'zoom-in';
    img.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    img.style.willChange = 'transform';
    img.style.transformOrigin = 'center center';

    let zoomed = false;

    const reset = () => {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
      img.style.cursor = 'zoom-in';
      zoomed = false;
    };

    img.addEventListener('click', (event) => {
      event.stopPropagation();
      if (zoomed) {
        reset();
        return;
      }
      const rect = img.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
      img.style.transform = `scale(${DESKTOP_ZOOM_LEVEL})`;
      img.style.cursor = 'zoom-out';
      zoomed = true;
    });

    // When zoomed, follow the cursor for a "magnifier" feel.
    img.addEventListener('mousemove', (event) => {
      if (!zoomed) return;
      const rect = img.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    });

    // Reset on slide change — GLightbox swaps the visible slide; ensure the
    // next view starts at fit-to-screen rather than mid-zoom.
    slideEl.addEventListener('mouseleave', reset);
  }

  destroy() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }
}

if (!window.__fullscreenProductZoom) {
  window.__fullscreenProductZoom = new FullscreenProductZoom();
}
